import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref } from 'vue';
import {
	AudioTechnology,
	MonetizationType,
	ObjectType,
	PresentationType,
	VideoTechnology,
} from '@/@types/graphql-types';
import { ClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import { useAppStore, useLanguageStore } from '@/helpers/composables/useStores';
import { BuyboxParam, getTranslatableForShowOfferGraphql, OfferLocation } from '@/helpers/offer-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { shouldClickMask, clickMaskUrl } from '@/components/click-masking/click-masking-config';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';

import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ModalHelper } from '@/helpers/modal-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { diffInDays, formatDateTime } from '@/helpers/date-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { SnowplowPackageContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { SponsoredClickoutsHelper } from '@/helpers/sponsored-clickouts-helper';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { getClickoutTrackingPlacement, OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';
import { useProviderPromotion } from '@/helpers/provider-promotion';

import { useBuyboxLoggedInExp } from '../experiments/BuyboxLoggedInExp/composables/useBuyboxLoggedInExp';

import { useInternationalOffers } from '../experiments/InternationalOffers/composables/useInternationalOffers';

import { faClock, faPlay, faTicket } from '@fortawesome/free-solid-svg-icons';
import { useAppleAmzChannelExp } from '../experiments/AppleAmzChannelExp';
import DisclaimerButton from '@/components/buybox-new/DisclaimerButton.vue';

import { useFewerPromotedTagsExpVariant } from '@/components/experiments/FewerPromotedTagsExp';

export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxOfferNew',
  props: {
    offer: null,
    title: null,
    isPromotedBuyBox: { type: Boolean },
    isBundle: { type: Boolean },
    isEpisode: { type: Boolean },
    seasonNumber: null,
    episodeNumber: null,
    additionalContexts: null,
    index: null
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		data: {
			buyboxOffer: TitleOfferFragment & { bundleId?: number };
			isBundle: boolean;
			isLeavingIconShown: boolean;
			row: HTMLElement | null;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offer: TitleOfferFragment & {
		bundleId?: number;
		bundleClearName?: string;
		bundleTechnicalName?: string;
		icons?: { iconWide: string; packageId: number }[];
		availableFromTime?: string;
		offerSeasons?: string[];
		minRetailPrice?: string;
	};
	title: TitleDetail;
	isPromotedBuyBox?: boolean;
	isBundle?: boolean;
	isEpisode?: boolean;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	additionalContexts: SnowplowContext[];
	// FEWER_PROMOTED_TAGS_EXP
	index?: number;
	// FEWER_PROMOTED_TAGS_EXP
};

const BuyBoxInfoModal = () => import('@/components/buybox-new/BuyBoxInfoModal.vue');
const BuyBoxBundleIcons = () => import('@/components/buybox-new/BuyBoxBundleIcons.vue');





const { country, language, webLocale } = useLanguageStore();
const { t, tc } = useI18n();
const { isArbitrating, withArbitrageTracking, trackTagEvent } = useArbitrage();
const { xServeTrack } = useAppStore();

// APPLE_AMZ_EXP
const { appleAmzChannelExpVariant, setAffiliateParamsAppleAmzExp } = useAppleAmzChannelExp(() => props.title);
// APPLE_AMZ_EXP

const displayBuyBoxInfoModal = ref<boolean>(false);
const offerContainer = ref<HTMLElement | null>(null);

const isModalProvider = computed(() => ['justwatchtv', 'plexplayer'].includes(props.offer.package.technicalName));

const { clickoutType, placement } = getClickoutTrackingPlacement(isModalProvider.value ? 'video_player' : 'regular');

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

const offerLocationInjection = inject<OfferLocation>('offerLocation');
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);

const { isPromotionOffer, providerPromotion: providerPromo } = useProviderPromotion();

const isProviderPromo = computed(() => !props.offer.bundleId && isPromotionOffer(props.offer));

const offerEntityId = computed(() => episodeEntityId ?? props.title.id);

const formattedDate = computed(() => {
	const ESTTimeZone = 'America/New_York';
	const PSTTimeZone = 'America/Los_Angeles';

	if (!props.offer?.availableFromTime) {
		return null;
	}

	return {
		Day: formatDateTime(new Date(props.offer.availableFromTime), language.value, country.value, {
			day: 'numeric',
			month: 'short',
			timeZone: ESTTimeZone,
		}),
		PST:
			formatDateTime(new Date(props.offer.availableFromTime), language.value, country.value, {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: PSTTimeZone,
			}) + ' PST',
		EST:
			formatDateTime(new Date(props.offer.availableFromTime), language.value, country.value, {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: ESTTimeZone,
			}) + ' EST',
	};
});

const shouldMaskClicks = computed(() => shouldClickMask(props.offer?.package.packageId, country.value));

const rowNumber = computed(() => {
	if (offerContainer.value == null) return undefined;

	const parentBuyBox = offerContainer.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(offerContainer.value));

	return index === -1 ? undefined : index;
});

// BUYBOX_LOGGED_IN_EXP
const { getProviderAffiliateParams } = useBuyboxLoggedInExp();
// BUYBOX_LOGGED_IN_EXP

// APPLE_AMZ_EXP
const updateAppleAmzExpClickoutUrl = computed(
	() =>
		appleAmzChannelExpVariant.value &&
		['avl', 'atp', 'amp', 'prv'].includes(props.offer.package.shortName) &&
		offerLocationInjection !== OfferLocation.PROMOTED_BUYBOX
);

// APPLE_AMZ_EXP

const showDisclaimerButton = computed(
	() =>
		isProviderPromo &&
		['AU', 'NZ'].includes(country.value) &&
		props.offer.package.packageId === 337 &&
		providerPromo.value.disclaimerText
);

const { rowCountry, trackInternationalOffers } = useInternationalOffers();

const offerUrl = computed(() => {
	let clickoutUrl = new ClickoutUrl(props.offer);

	if (isProviderPromo.value && providerPromo.value?.url) {
		const offer = { ...props.offer, standardWebURL: providerPromo.value.url };
		clickoutUrl = new ClickoutUrl(offer, { skipRedirect: true });
	}
	if (shouldMaskClicks.value) {
		const maskedUrl = clickMaskUrl(props.offer.standardWebURL!);
		const offer = { ...props.offer, standardWebURL: maskedUrl };
		clickoutUrl = new ClickoutUrl(offer, { skipRedirect: true });
	}

	if (isArbitrating.value) {
		const arbitrageUrl = withArbitrageTracking(() => props.offer);
		if (arbitrageUrl) {
			const finalUrl = arbitrageUrl.toString();
			clickoutUrl = new ClickoutUrl({ ...props.offer, standardWebURL: finalUrl }, { skipRedirect: true });
		}
	}
	// APPLE_AMZ_EXP
	if (updateAppleAmzExpClickoutUrl.value) {
		clickoutUrl = new ClickoutUrl(props.offer, { skipYieldOptimization: true });
	}
	// APPLE_AMZ_EXP

	const url = clickoutUrl
		.addContexts(titleContext.value)
		.setForTracking('offerLocation', offerLocationInjection)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
		.setTitleSourceId(titleSourceId)
		.set('uct_country', country.value)
		.set('uct_buybox', props.isPromotedBuyBox ? BuyboxParam.Promoted : BuyboxParam.Normal)
		.updateContext('TitleContextGraphql', 'offerEntityId', offerEntityId.value)
		.setCanary(xServeTrack.value);

	if (rowCountry.value) {
		url.set('uct_country', rowCountry.value);
	}

	if (props.isBundle) {
		const packageContext = new SnowplowPackageContext(
			props.offer.bundleId ?? props.offer.package.packageId,
			props.offer.bundleClearName ?? props.offer.package.technicalName,
			'bundle'
		);
		clickoutUrl.addContexts(packageContext);

		// Bypass UCT for US users on bundle amazonstarzamazonhbomax
		if (props.offer.bundleTechnicalName === 'amazonstarzamazonhbomax') {
			url.set('uct_ct', 'bypass');
		}
	}

	// Amazon streaming bounty redirects
	if ([9, 119].includes(props.offer.package.packageId)) {
		const redirect = url.get('r');
		url.set('r', redirect!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	// APPLE_AMZ_EXP
	if (updateAppleAmzExpClickoutUrl.value) {
		return setAffiliateParamsAppleAmzExp(url, 'ro', props.offer.package.shortName);
	}
	// APPLE_AMZ_EXP

	// BUYBOX_LOGGED_IN_EXP
	const affiliateParams = getProviderAffiliateParams(props.offer.package.shortName, 'RO');
	if (affiliateParams) {
		url.set('uct_ct', affiliateParams.ct);
		if (affiliateParams.itsct) {
			url.set('uct_itsct', affiliateParams.itsct);
		}
	}
	// BUYBOX_LOGGED_IN_EXP

	return url;
});

const isLeavingIconShown = computed(() => {
	return (
		!!props.offer.availableTo &&
		diffInDays(new Date(props.offer.availableTo)) < 30 &&
		diffInDays(new Date(props.offer.availableTo)) >= 0
	);
});

const leavingLabel = computed(() => {
	if (!isLeavingIconShown.value) return '';
	const diffDays = diffInDays(new Date(props.offer.availableTo));
	if (diffDays === 0) return tc('WEBAPP_LEAVING_TODAY');
	if (diffDays === 1) return tc('WEBAPP_LEAVING_TOMORROW');
	return t('WEBAPP_LEAVING_X_DAYS', { days: diffDays });
});

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

const audioLanguages = computed(() => {
	if (!props.offer?.audioLanguages) return false;
	return languageArrayToString(props?.offer?.audioLanguages);
});

const selectedPackagePlan = computed(() => {
	if (props.offer?.package.planOffers.length > 0) {
		const plan = props.offer.package.planOffers[0];
		// if a plan has a children that means it's a free trial plan and that always has priority.
		if (plan.children.length > 0) {
			return { ...plan.children[0], retailPrice: plan.retailPrice };
		}
		return plan;
	}
	return null;
});

const retailPrice = computed(() => {
	if (props.isBundle) {
		return null;
	} else if (props.offer?.retailPrice) {
		return props.offer?.retailPrice;
	} else if (selectedPackagePlan.value && selectedPackagePlan.value.retailPrice) {
		// Retail price from API
		return t(
			props.offer.monetizationType === MonetizationType.Flatrate && hasfreeTrial.value
				? 'WEBAPP_THEN_X_PER_MONTH'
				: 'WEBAPP_X_PER_MONTH',
			{ price: selectedPackagePlan.value.retailPrice }
		);
	}
	return null;
});

const isShowPageTVOD = computed(
	() =>
		[MonetizationType.Buy, MonetizationType.Rent].includes(props.offer.monetizationType) &&
		props.title.objectType === ObjectType.Show &&
		!props.isEpisode
);

const minEpisodeTVODPriceOnShows = computed(() => {
	if (!isShowPageTVOD.value || props.title.__typename !== 'Show' || !props.offer.minRetailPrice) return null;

	return props.offer.minRetailPrice;
});

const minSeasonTVODPriceOnShows = computed(() => {
	const seasonIdsOnOffer = props.offer.offerSeasons ?? [];
	if (
		props.title.__typename !== 'Show' ||
		!seasonIdsOnOffer.length ||
		![MonetizationType.Buy, MonetizationType.Rent].includes(props.offer.monetizationType)
	)
		return null;

	// all seasonIds that are on the Show offer
	const filteredSeasons = props.title.seasons.filter(season => seasonIdsOnOffer.includes(season.id));
	if (!filteredSeasons.length) return null;

	// find the lowest price of all seasons
	const formattedMinPrice = filteredSeasons
		.map(season =>
			season.offers.find(
				offer =>
					offer.monetizationType === props.offer.monetizationType &&
					props.offer.package.shortName === offer.package.shortName
			)
		)
		.filter(offer => offer?.retailPrice != null && offer?.retailPriceValue != null && offer?.retailPriceValue !== 0)
		.sort((a, b) => a!.retailPriceValue! - b!.retailPriceValue!)[0]?.retailPrice;

	return formattedMinPrice;
});

const hasMinPriceTVOD = computed(() => !!minEpisodeTVODPriceOnShows || !!minSeasonTVODPriceOnShows);

const showRetailPrice = computed(() => !!retailPrice.value && props.offer?.monetizationType !== MonetizationType.Free);

const hasfreeTrial = computed(
	() => !!freeTrialText.value && props.offer?.monetizationType === MonetizationType.Flatrate
);

const freeTrialText = computed(() => {
	if (props.isBundle) {
		return null;
	}
	// Free trial data from API
	if (selectedPackagePlan.value?.isTrial) {
		if (selectedPackagePlan.value.durationDays === 1) return t('WEBAPP_1_DAY_FREE');
		return t('WEBAPP_X_DAYS_FREE', {
			days: selectedPackagePlan.value.durationDays,
		});
	}
	return null;
});

const presentationTypeLabel = computed(() => {
	switch (props.offer?.presentationType) {
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'DVD';
		case PresentationType.Bluray:
			return 'Blu-Ray';
		default:
			return '';
	}
});

const ageRating = computed(() => {
	if (props.title.__typename !== 'Season') {
		return props.title.content.ageCertification;
	}
	return null;
});

const dolbyTechnology = computed(() => {
	return (
		props.offer?.videoTechnology.includes(VideoTechnology.DolbyVision) ||
		props.offer?.audioTechnology.includes(AudioTechnology.Dolby_5Point_1) ||
		props.offer?.audioTechnology.includes(AudioTechnology.DolbyAtmos)
	);
});

const showText = computed(() => getTranslatableForShowOfferGraphql(props.offer, props.title.objectType));

const runtimeMins = computed(() => (props.title?.content?.runtime ? `${props.title.content.runtime}min` : null));

// FEWER_PROMOTED_TAGS_EXP
const { variant: fewerPromotedTagsExpVariant } = useFewerPromotedTagsExpVariant(
	props.title.objectType,
	props.title.offers
);
// FEWER_PROMOTED_TAGS_EXP

const highlightCta = computed(() => {
	// FEWER_PROMOTED_TAGS_EXP
	switch (fewerPromotedTagsExpVariant.value) {
		case 'variant_2':
			return (props.index || 0) < 3;
	}
	// FEWER_PROMOTED_TAGS_EXP
	return props.offer?.monetizationType === MonetizationType.Flatrate && !props.isBundle;
});

const isSponsoredOffer = computed(() =>
	SponsoredClickoutsHelper.isSponsoredOfferGraphql(props.offer?.package.packageId, webLocale.value)
		? 'nofollow sponsored noopener'
		: 'nofollow noopener'
);

const playerModalByProvider: Record<string, { cmp: () => Promise<any> }> = {
	plexplayer: {
		cmp: () => import('@/ui-components/PlexPlayerModal.vue'),
	},
	justwatchtv: {
		cmp: () => import('@/ui-components/JwTVModal.vue'),
	},
};

function openModal() {
	if (!isModalProvider.value) return;
	const modalData = playerModalByProvider[props.offer.package.technicalName];
	ModalHelper.openModal(
		modalData.cmp,
		{ offer: props.offer, title: props.title },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	trackClickout(offerUrl.value);
	handleClickOut();
}

function toggleBuyBoxInfoModal(display: boolean) {
	displayBuyBoxInfoModal.value = display;

	if (displayBuyBoxInfoModal.value) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'more_info_clicked', label: 'buybox' },
			props.additionalContexts
		);
	}
}

function handleClickOut() {
	trackTagEvent(() => props.offer);

	trackInternationalOffers({
		action: 'click',
		label: 'clickout_' + (rowCountry.value ?? country.value),
	});

	emit('clickOut', {
		buyboxOffer: props.offer,
		isBundle: !!props.isBundle,
		isLeavingIconShown: isLeavingIconShown.value,
		row: offerContainer.value,
	});
}

function onVisibilityChanged(isVisible: boolean) {
	onIntersection(offerUrl.value, isVisible);
}

function languageArrayToString(languageArray: string[]) {
	const languages = languageArray.map(language => {
		return {
			language,
			name: tc(`WEBAPP_LANGUAGE_${language.toUpperCase()}`),
		};
	});
	const filteredLanguages = languages.filter((language: { language: string; name: string }) => {
		return !language.name.startsWith('WEBAPP_LANGUAGE_');
	});
	return filteredLanguages
		.map((language: { language: string; name: string }) => {
			return language.name;
		})
		.join(', ');
}

function offerLabel(monetizationType: MonetizationType) {
	if (isProviderPromo.value) return providerPromo.value!.offerText;

	const monetizationTypeMapping = {
		ADS: tc('WEBAPP_FREE_WITH_ADS'),
		BUY: tc('WEBAPP_BUY'),
		CINEMA: tc('WEBAPP_CINEMA'),
		FAST: tc('WEBAPP_FREE_TV'),
		FLATRATE: tc('WEBAPP_SUBSCRIPTION'),
		FLATRATE_AND_BUY: tc('WEBAPP_BUY'),
		FREE: tc('WEBAPP_FREE'),
		RENT: tc('WEBAPP_RENT'),
	};
	if (props.isBundle) {
		return tc('WEBAPP_BUNDLE');
	}

	if (monetizationType === MonetizationType.Fast && formattedDate.value?.['Day']) {
		return formattedDate.value?.['Day'];
	}

	return monetizationTypeMapping[monetizationType];
}

return { __sfc: true,BuyBoxInfoModal, BuyBoxBundleIcons, props, emit, country, language, webLocale, t, tc, isArbitrating, withArbitrageTracking, trackTagEvent, xServeTrack, appleAmzChannelExpVariant, setAffiliateParamsAppleAmzExp, displayBuyBoxInfoModal, offerContainer, isModalProvider, clickoutType, placement, onIntersection, offerLocationInjection, titleContext, filterOption, titleSourceId, episodeEntityId, isPromotionOffer, providerPromo, isProviderPromo, offerEntityId, formattedDate, shouldMaskClicks, rowNumber, getProviderAffiliateParams, updateAppleAmzExpClickoutUrl, showDisclaimerButton, rowCountry, trackInternationalOffers, offerUrl, isLeavingIconShown, leavingLabel, observeVisibilityOptions, audioLanguages, selectedPackagePlan, retailPrice, isShowPageTVOD, minEpisodeTVODPriceOnShows, minSeasonTVODPriceOnShows, hasMinPriceTVOD, showRetailPrice, hasfreeTrial, freeTrialText, presentationTypeLabel, ageRating, dolbyTechnology, showText, runtimeMins, fewerPromotedTagsExpVariant, highlightCta, isSponsoredOffer, playerModalByProvider, openModal, toggleBuyBoxInfoModal, handleClickOut, onVisibilityChanged, languageArrayToString, offerLabel, ClickoutAnchor, ProviderIcon, faClock, faPlay, faTicket, DisclaimerButton }
}

})